import { ref } from "vue";
import axios from "axios";

/**
 * Prepares and returns chart-ready data and historical KPI data for UI components.
 *
 * Behavior:
 * - Reads the current KPI from `sessionStorage`
 * - Generates mock historical data.
 * - Builds an initial `kpiHistoricalData` containing mock `value`, `timestamp`, and `statistics`.
 * - Calls the real API.
 *   - If the response contains historical values, replaces the mock data
 *     in `kpiHistoricalData` with the response data and sets `mockupAlert.value = false`.
 *   - Errors are logged to the console.
 * - Builds `chartData` compatible with Chart.js:
 *   - `labels` are generated by formatting timestamps via `formatDateToLocale` default 'en-EN').
 *   - `datasets` contain a single dataset using the KPI name and values.
 *
 * Returns (Promise resolving to):
 *   {
 *     chartData: { labels: string[], datasets: [{ label: string, data: number[] }] },
 *     kpiHistoricalData: Ref({ message, data: { value: number[], timestamp: string[], statistics: {min, max, avg} }, status }),
 *     mockupAlert: Ref<boolean>
 *   }
 */
export async function fetchChartData(apiUrl, realm) {
  const rawKpi = sessionStorage.getItem("kpi");
  const kpi = rawKpi ? JSON.parse(rawKpi) : {};

  const kpiName = ref(kpi.name || "unknown");
  const isQualitative = kpi?.definition?.valueType === "QUALITATIVE";
  const allowedValues = kpi?.definition?.allowedValues || [];
  const targetValue = kpi?.definition?.targetValue;
  const isManualKpi = String(kpi.kpiType || "").toLowerCase() === "manual";
  const manualInput = kpi?.definition?.manualInput || [];

  function createArrayOfRandomValues(centerValue) {
    const base = Number.isFinite(centerValue) ? centerValue : 0;
    let array = [base];
    let increment = Math.max(5, Math.floor(Math.abs(base) / 2));
    let usedValues = new Set([base]);

    // Safety counter to prevent infinite loops
    let attempts = 0;
    const maxAttempts = 100;

    while (usedValues.size < 12 && attempts < maxAttempts) {
      attempts++;
      let randomIncrement = Math.floor(Math.random() * increment) + 1;
      let newValue =
        base + (Math.random() < 0.5 ? -randomIncrement : randomIncrement);
      if (!usedValues.has(newValue)) {
        usedValues.add(newValue);
        array.push(newValue);
      }
      
      // If we're struggling to find unique values, expand the range
      if (attempts % 10 === 0) {
        increment = Math.max(increment, usedValues.size * 2);
      }
    }
    
    // Fill remaining slots if we couldn't generate 12 unique values
    while (array.length < 12) {
      array.push(base);
    }
    
    for (let i = array.length - 1; i > 0; i--) {
      let j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  function generateQualitativeData(allowedValues, targetValue) {
    if (!allowedValues || allowedValues.length === 0) {
      return [];
    }

    const targetIndex = allowedValues.indexOf(targetValue);
    const frequencies = [];

    allowedValues.forEach((value, index) => {
      let frequency = Math.floor(Math.random() * 13) + 3;

      if (index === targetIndex && targetIndex !== -1) {
        frequency = Math.floor(frequency * (1.3 + Math.random() * 0.2));
      }

      frequencies.push(frequency);
    });

    return frequencies;
  }

  function generateColors(count) {
    const colors = [
      "#4CAF50", // Verde per target
      "#2196F3", // Blu
      "#FFC107", // Giallo
      "#FF5722", // Rosso-arancio
      "#9C27B0", // Viola
      "#00BCD4", // Ciano
      "#FF9800", // Arancione
      "#607D8B", // Grigio-blu
    ];

    if (count > colors.length) {
      for (let i = colors.length; i < count; i++) {
        const hue = (i * 360) / count;
        const s = 70;
        const l = 50;
        
        // Convert HSL to RGB
        const c = (1 - Math.abs(2 * l / 100 - 1)) * s / 100;
        const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
        const m = l / 100 - c / 2;
        
        let r = 0, g = 0, b = 0;
        if (hue < 60) { r = c; g = x; b = 0; }
        else if (hue < 120) { r = x; g = c; b = 0; }
        else if (hue < 180) { r = 0; g = c; b = x; }
        else if (hue < 240) { r = 0; g = x; b = c; }
        else if (hue < 300) { r = x; g = 0; b = c; }
        else { r = c; g = 0; b = x; }
        
        r = Math.round((r + m) * 255);
        g = Math.round((g + m) * 255);
        b = Math.round((b + m) * 255);
        
        // Convert to hex
        const toHex = (n) => n.toString(16).padStart(2, '0');
        colors.push(`#${toHex(r)}${toHex(g)}${toHex(b)}`);
      }
    }

    return colors.slice(0, count);
  }

  function calculateStats(values) {
    if (!Array.isArray(values) || values.length === 0) {
      return { min: 0, max: 0, avg: 0 };
    }
    let min = Math.min(...values);
    let max = Math.max(...values);
    let sum = values.reduce((acc, val) => acc + val, 0);
    let avg = sum / values.length;
    return { min, max, avg };
  }

  function generateTimestamps(schedule) {
    const { time, value } = schedule || { time: "day", value: 1 };
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 1);
    const timestamps = [];
    const timeUnits = {
      second: 1000,
      minute: 60000,
      hour: 3600000,
      day: 86400000,
    };
    const unit = timeUnits[time] ? time : "day";
    const unitMs = timeUnits[unit];
    const step = Number.isFinite(value) && value > 0 ? value : 1;
    for (let i = 0; i < 12; i++) {
      const newTimestamp = new Date(startDate.getTime() + i * step * unitMs);
      timestamps.push(newTimestamp.toISOString());
    }
    return timestamps;
  }

  const targetValueNum = Number.isFinite(kpi?.definition?.targetValue)
    ? kpi.definition.targetValue
    : 0;
  const scheduleValue = kpi?.formula?.job?.schedule ?? {
    time: "day",
    value: 1,
  };

  const randomValues = isQualitative
    ? generateQualitativeData(allowedValues, targetValue)
    : createArrayOfRandomValues(targetValueNum);
  const stats = calculateStats(randomValues);
  const timestamps = isQualitative
    ? allowedValues
    : generateTimestamps(scheduleValue);

  // For Manual KPI, always use manualInput as initial data (regardless of ID)
  let initialValues = randomValues;
  let initialTimestamps = timestamps;
  let initialStats = stats;
  let initialMockup = true;

  if (isManualKpi) {
    if (isQualitative && manualInput.length > 0) {
      // For qualitative KPIs, count frequency of each allowed value
      const frequencyMap = {};
      allowedValues.forEach(val => frequencyMap[val] = 0);
      
      manualInput.forEach((input) => {
        if (input.value && frequencyMap[input.value] !== undefined) {
          frequencyMap[input.value]++;
        }
      });
      
      const frequencies = allowedValues.map(val => frequencyMap[val]);
      
      if (frequencies.some(f => f > 0)) {
        initialValues = frequencies;
        initialTimestamps = allowedValues;
        initialStats = calculateStats(frequencies);
        initialMockup = false; // Don't show mockup alert for manual KPI with data
      }
    } else if (manualInput.length > 0) {
      // For quantitative KPIs
      const manualValues = manualInput
        .filter((input) => input.value !== undefined)
        .map((input) => input.value);
      const manualTimestamps = manualInput
        .filter((input) => input.timestamp)
        .map((input) => input.timestamp);
      
      if (manualValues.length > 0) {
        initialValues = manualValues;
        initialTimestamps = manualTimestamps;
        initialStats = calculateStats(manualValues);
        initialMockup = false; // Don't show mockup alert for manual KPI with data
      }
    }
  }

  const kpiHistoricalData = ref({
    message: "HistoricalData",
    data: {
      value: initialValues,
      timestamp: initialTimestamps,
      statistics: initialStats,
    },
    status: 200,
  });

  const mockupAlert = ref(initialMockup);

  // Only fetch from API if KPI has an ID (not in creation phase)
  if (kpi.id) {
    try {
      const response = await axios.get(
        `${apiUrl}/${realm}/historical-data/query/all`,
        {
          withCredentials: true,
          params: {
            kpiId: kpi.id,
            bucket: realm,
            measurement: kpiName.value,
            kpimEngineRequest: false,
          },
        },
      );

      if (response?.data?.data?.value && response.data.data.value.length > 0) {
        // For Manual KPI, use only manualInput (ignore API data)
        // For non-Manual KPI, use API data
        if (!isManualKpi) {
          kpiHistoricalData.value.data = response.data.data;
          mockupAlert.value = false;
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  const labels = isQualitative
    ? kpiHistoricalData.value?.data?.timestamp || []
    : (kpiHistoricalData.value?.data?.timestamp || []).map((item) =>
        formatDateToLocale(item),
      );

  const colors = isQualitative ? generateColors(labels.length) : undefined;

  // Create dataset for chart
  const datasets = [
    {
      label: kpiName.value,
      data: kpiHistoricalData.value?.data?.value || [],
      ...(isQualitative && {
        backgroundColor: colors,
        borderColor: colors,
      }),
    },
  ];

  const chartData = {
    labels,
    datasets,
  };

  return { chartData, kpiHistoricalData, mockupAlert };
}

export function formatDateToLocale(dateString) {
  try {
    const date = new Date(dateString);
    const userLocale = localStorage.getItem("lang") || "en-EN";
    return date.toLocaleString(userLocale);
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
}
